import type { ReportColumn } from '../models/column.types'

export interface MapColumnsContext {
  getColumn: (field: string) => ReportColumn | null | undefined
  isAvailable?: (column: ReportColumn) => boolean
  fallbackHeader?: (field: string) => string
  shopName?: string | null
  appName?: string | null
}

function fallbackHeader(field: string): string {
  return field.replaceAll(/[_-]+/gu, ' ').replaceAll(/\b\p{L}/gu, (letter) => letter.toUpperCase())
}

type PrefixRule = {
  test: (field: string) => boolean
  img: string | null
  prefix: (context: MapColumnsContext) => string
}

function sourcePrefix(name: string | null | undefined): string {
  const value = name?.trim()
  return value ? `${value} ` : ''
}

const PREFIX_RULES: readonly PrefixRule[] = [
  {
    test: (field) => field.startsWith('organic_'),
    img: 'search_console',
    prefix: () => 'Search Console ',
  },
  { test: (field) => field.startsWith('fp_'), img: 'bq', prefix: () => 'BQ ' },
  { test: (field) => field.startsWith('sheet_'), img: 'sheet', prefix: () => 'Sheet ' },
  {
    test: (field) => field.startsWith('ep_'),
    img: 'shop',
    prefix: (context) => sourcePrefix(context.shopName),
  },
  {
    test: (field) =>
      field.startsWith('mmp_') || ['network_cost', 'ecpi', 'skad_ecpi'].includes(field),
    img: 'adjust',
    prefix: (context) => sourcePrefix(context.appName),
  },
  { test: (field) => field.startsWith('mp_'), img: 'ga4', prefix: () => 'GA4 ' },
  { test: (field) => field.startsWith('cvr_'), img: 'adjust', prefix: () => 'Adjust ' },
  { test: (field) => field.startsWith('calculated_'), img: null, prefix: () => '' },
  { test: (field) => field.startsWith('hardal_'), img: 'hardal', prefix: () => 'Hardal ' },
  { test: (field) => field.startsWith('iap_'), img: 'adapty', prefix: () => 'Adapty ' },
]

function prefixedHeader(header: string, prefix: string): string {
  return prefix && !header.startsWith(prefix) ? `${prefix}${header}` : header
}

function applyPrefixRule(column: ReportColumn, context: MapColumnsContext): ReportColumn {
  const rule = PREFIX_RULES.find(({ test }) => test(column.field))
  if (!rule) return column

  if (rule.img === null) {
    const next = {
      ...column,
      header: prefixedHeader(column.header, rule.prefix(context)),
      isCalculated: true,
    }
    delete next.headerImage
    return next
  }

  return {
    ...column,
    header: prefixedHeader(column.header, rule.prefix(context)),
    headerImage: rule.img,
  }
}

function group(column: ReportColumn): number {
  if (column.isImage || column.sub_type === 'image') return 0
  return column.numeric ? 2 : 1
}

/** Pure catalog mapper with injected availability and deterministic image→dimension→metric order. */
export function mapColumns(fields: readonly string[], context: MapColumnsContext): ReportColumn[] {
  const seen = new Set<string>()
  const mapped: ReportColumn[] = []

  for (const field of fields) {
    if (!field || seen.has(field)) continue
    seen.add(field)
    const catalogColumn = context.getColumn(field)
    const column: ReportColumn = catalogColumn
      ? { ...catalogColumn }
      : { field, header: context.fallbackHeader?.(field) ?? fallbackHeader(field) }
    const handledColumn = applyPrefixRule(column, context)
    if (context.isAvailable?.(handledColumn) === false) continue
    mapped.push(handledColumn)
  }

  return mapped
    .map((column, index) => ({ column, index }))
    .sort((left, right) => group(left.column) - group(right.column) || left.index - right.index)
    .map(({ column }, index) => ({
      ...column,
      ...(column.frozen === undefined && index === 0 ? { frozen: true } : {}),
    }))
}
